import { useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import "./DownloadPage.css";
import { appVersion, installerDownloadUrl } from "../../api/http";
import deployKitIcon from "../../assets/brand/deploykit-icon.png";
import appPreview from "../../assets/brand/deploykit-app-preview-v2.png";
import packagingPreview from "../../assets/guide/guide-current-packaging.png";
import terminalPreview from "../../assets/guide/guide-current-terminal.png";
import historyPreview from "../../assets/guide/guide-current-history.png";
import pathManagePreview from "../../assets/guide/guide-current-path-manage.png";

type DownloadStats = {
    count: number;
    updatedAt?: string | null;
};

type ProductIconName = "package" | "server" | "terminal" | "history" | "download";

const DOWNLOAD_STATS_URL = "/api/download/stats";
const DOWNLOAD_STATS_STREAM_URL = "/api/download/stats/stream";
const DOWNLOAD_STATS_POLL_INTERVAL = 5_000;

const ProductIcon = ({ name }: { name: ProductIconName }) => {
    const paths: Record<ProductIconName, ReactNode> = {
        package: (
            <>
                <path d="m12 3 8 4.5v9L12 21l-8-4.5v-9L12 3Z" />
                <path d="m4.5 7.7 7.5 4.2 7.5-4.2M12 12v8.5" />
            </>
        ),
        server: (
            <>
                <rect x="3" y="4" width="18" height="6" rx="2" />
                <rect x="3" y="14" width="18" height="6" rx="2" />
                <path d="M7 7h.01M7 17h.01M16 7h2M16 17h2" />
            </>
        ),
        terminal: (
            <>
                <rect x="3" y="4" width="18" height="16" rx="3" />
                <path d="m7 9 3 3-3 3M13 15h4" />
            </>
        ),
        history: (
            <>
                <path d="M3 12a9 9 0 1 0 3-6.7L3 8" />
                <path d="M3 4v4h4M12 7v5l3 2" />
            </>
        ),
        download: (
            <>
                <path d="M12 3v12M7 10l5 5 5-5" />
                <path d="M5 21h14" />
            </>
        ),
    };

    return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
            {paths[name]}
        </svg>
    );
};

const DownloadPage = () => {
    const pageRef = useRef<HTMLElement>(null);
    const [downloadState, setDownloadState] = useState<"idle" | "checking">("idle");
    const [downloadMessage, setDownloadMessage] = useState("");
    const [downloadCount, setDownloadCount] = useState<number | null>(null);

    const formattedDownloadCount = useMemo(
        () => (downloadCount === null ? null : new Intl.NumberFormat("ko-KR").format(downloadCount)),
        [downloadCount]
    );

    useEffect(() => {
        let disposed = false;
        let eventSource: EventSource | null = null;
        let pollTimer: number | null = null;

        const applyStats = (stats: DownloadStats) => {
            if (!disposed && Number.isFinite(stats.count) && stats.count >= 0) {
                setDownloadCount(stats.count);
            }
        };

        const loadStats = async () => {
            try {
                const response = await fetch(DOWNLOAD_STATS_URL, { cache: "no-store" });
                if (!response.ok) return;
                applyStats((await response.json()) as DownloadStats);
            } catch (error) {
                console.info("download stats load failed", error);
            }
        };

        const startPolling = () => {
            if (pollTimer !== null) return;
            pollTimer = window.setInterval(loadStats, DOWNLOAD_STATS_POLL_INTERVAL);
        };

        loadStats();

        if (typeof EventSource === "undefined") {
            startPolling();
        } else {
            eventSource = new EventSource(DOWNLOAD_STATS_STREAM_URL);
            eventSource.addEventListener("download-count", (event) => {
                try {
                    applyStats(JSON.parse((event as MessageEvent<string>).data) as DownloadStats);
                } catch (error) {
                    console.info("download stats stream message failed", error);
                }
            });
            eventSource.onerror = () => {
                eventSource?.close();
                eventSource = null;
                startPolling();
            };
        }

        return () => {
            disposed = true;
            eventSource?.close();
            if (pollTimer !== null) window.clearInterval(pollTimer);
        };
    }, []);

    useEffect(() => {
        const page = pageRef.current;
        if (!page) return;

        const revealItems = Array.from(page.querySelectorAll<HTMLElement>("[data-reveal]"));
        page.classList.add("is-motion-ready");

        if (!("IntersectionObserver" in window)) {
            revealItems.forEach((item) => item.classList.add("is-visible"));
            return;
        }

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (!entry.isIntersecting) return;
                    entry.target.classList.add("is-visible");
                    observer.unobserve(entry.target);
                });
            },
            { threshold: 0.12, rootMargin: "0px 0px -56px" }
        );

        revealItems.forEach((item) => observer.observe(item));

        return () => observer.disconnect();
    }, []);

    const handleDownload = async (event: React.MouseEvent<HTMLAnchorElement>) => {
        event.preventDefault();
        if (downloadState === "checking") return;

        setDownloadState("checking");
        setDownloadMessage("");

        try {
            const response = await fetch(installerDownloadUrl, { method: "HEAD", cache: "no-store" });
            if (!response.ok) {
                throw new Error(`Installer check failed: ${response.status}`);
            }

            window.location.href = installerDownloadUrl;
        } catch (error) {
            console.error("installer download check failed", error);
            setDownloadMessage("설치 파일이 아직 서버에 준비되지 않았습니다. 잠시 후 다시 시도해 주세요.");
        } finally {
            setDownloadState("idle");
        }
    };

    const downloadLabel = downloadState === "checking" ? "파일 확인 중..." : "Windows용 DeployKit 다운로드";

    return (
        <main className="download-page" ref={pageRef}>
            <header className="download-nav">
                <a className="download-nav-brand" href="#top" aria-label="DeployKit 홈">
                    <img src={deployKitIcon} alt="" aria-hidden="true" />
                    <span>DeployKit</span>
                </a>

                <nav className="download-nav-links" aria-label="페이지 메뉴">
                    <a href="#features">기능</a>
                    <a href="#workflow">사용 방법</a>
                    <a href="#installation">설치 안내</a>
                    <a href="#example">사용 예시</a>
                </nav>

            </header>

            <section className="download-hero" id="top">
                <div className="download-hero-copy">
                    <span className="download-eyebrow">
                        <i aria-hidden="true" />
                        <span>DeployKit · Windows</span>
                        <strong>FOR JAVA / KOTLIN</strong>
                    </span>
                    <h1>
                        Git·SVN 변경 파일을 추출해
                        <span>배포 패키지를 만듭니다.</span>
                    </h1>
                    <p className="download-lead">
                        DeployKit은 Git 커밋, SVN 리비전, 일반 폴더에서 변경 파일을 추출하고 필요한 배포 파일만
                        골라 서버 경로 구조를 유지한 ZIP으로 만드는 Windows 로컬 배포 도구입니다. 패키지는
                        SSH/SFTP로 전송할 수 있고 배포·백업·복구 스크립트도 함께 생성합니다.
                    </p>

                    <div className="download-actions">
                        <a className="download-primary" href={installerDownloadUrl} onClick={handleDownload}>
                            <ProductIcon name="download" />
                            <span>{downloadLabel}</span>
                        </a>
                        <a
                            className="download-secondary"
                            href="https://github.com/Backjinuk/DeployKit-Desktop"
                            target="_blank"
                            rel="noreferrer"
                        >
                            GitHub에서 보기
                            <span aria-hidden="true">↗</span>
                        </a>
                    </div>

                    <div className="download-meta" aria-live="polite">
                        <span>v{appVersion}</span>
                        <span>Windows 10/11</span>
                        <strong>
                            <i className="download-live-dot" aria-hidden="true" />
                            {formattedDownloadCount === null
                                ? "다운로드 집계 중"
                                : `누적 다운로드 ${formattedDownloadCount}회`}
                        </strong>
                    </div>

                    {downloadMessage && <p className="download-message">{downloadMessage}</p>}
                </div>

                <div className="download-hero-product" aria-label="DeployKit 2.0 애플리케이션 미리보기">
                    <div className="download-window">
                        <div className="download-window-bar">
                            <div className="download-window-dots" aria-hidden="true">
                                <span />
                                <span />
                                <span />
                            </div>
                            <strong>DeployKit Desktop 2.0</strong>
                            <em>패키징 · 서버 전송 · 터미널</em>
                        </div>
                        <img src={appPreview} alt="DeployKit 패키징 워크스페이스 화면" />
                    </div>
                </div>
            </section>

            <section className="download-showcase" id="features">
                <div className="download-section-heading" data-reveal>
                    <span>주요 기능</span>
                    <h2>DeployKit에서 할 수 있는 작업</h2>
                    <p>
                        변경 파일 조회부터 패키지 생성, 서버 전송과 배포 이력 관리까지 필요한 기능을 정리했습니다.
                    </p>
                </div>

                <div className="download-bento-grid">
                    <article className="download-bento-card is-wide is-packaging" data-reveal>
                        <div className="download-card-copy">
                            <span className="download-card-icon"><ProductIcon name="package" /></span>
                            <small>Git·SVN 패키징</small>
                            <h3>변경 파일을 추출하고<br />배포 ZIP을 만듭니다.</h3>
                            <p>
                                날짜와 Git 커밋, SVN 리비전, 로컬 수정일을 기준으로 변경 파일을 모읍니다.
                                선택한 배포 파일은 서버 경로 구조를 유지한 ZIP과 배포·백업·복구용 patch.sh로
                                생성합니다. Java 소스는 지정한 JDK로 컴파일해 배포 가능한 class 산출물로 패키징합니다.
                            </p>
                            <ul>
                                <li>버전·파일 단위 선택</li>
                                <li>배포·백업·복구 스크립트</li>
                                <li>JVM 컴파일 캐시</li>
                            </ul>
                        </div>
                        <div className="download-card-visual">
                            <img src={packagingPreview} alt="날짜, 버전, 파일을 선택하는 DeployKit 패키징 화면" />
                        </div>
                    </article>

                    <article className="download-bento-card is-terminal" data-reveal>
                        <div className="download-card-copy">
                            <span className="download-card-icon"><ProductIcon name="terminal" /></span>
                            <small>터미널</small>
                            <h3>로컬과 원격 터미널을 함께 엽니다.</h3>
                            <p>배포 경로별 로컬 PowerShell과 SSH 원격 터미널을 탭으로 열 수 있습니다.</p>
                        </div>
                        <div className="download-card-visual">
                            <img src={terminalPreview} alt="DeployKit의 로컬 PowerShell 터미널 화면" />
                        </div>
                    </article>

                    <article className="download-bento-card is-history" data-reveal>
                        <div className="download-card-copy">
                            <span className="download-card-icon"><ProductIcon name="history" /></span>
                            <small>배포 이력</small>
                            <h3>이전 패키지를 다시 만들거나 전송합니다.</h3>
                            <p>패키징과 전송 결과를 기록하고 이전 조건 그대로 재패키징하거나 재전송합니다.</p>
                        </div>
                        <div className="download-card-visual">
                            <img src={historyPreview} alt="DeployKit 배포 이력 화면" />
                        </div>
                    </article>

                    <article className="download-bento-card is-wide is-paths" data-reveal>
                        <div className="download-card-copy">
                            <span className="download-card-icon"><ProductIcon name="server" /></span>
                            <small>경로 관리</small>
                            <h3>프로젝트와 서버 경로를<br />등록해 두고 사용합니다.</h3>
                            <p>
                                로컬 프로젝트, 대상 서버 경로, JDK, SSH 프로필을 배포 경로에 연결합니다.
                                비밀번호 인증과 개인키 인증, 호스트 키 검증을 지원합니다.
                            </p>
                        </div>
                        <div className="download-card-visual">
                            <img src={pathManagePreview} alt="DeployKit 배포 경로 관리 화면" />
                        </div>
                    </article>
                </div>
            </section>

            <section className="download-workflow" id="workflow">
                <div className="download-section-heading" data-reveal>
                    <span>사용 방법</span>
                    <h2>기본 사용 순서</h2>
                    <p>경로를 등록한 뒤에는 아래 순서로 패키지를 만들고 전송합니다.</p>
                </div>

                <div className="download-workflow-grid">
                    <article data-reveal>
                        <span>01</span>
                        <div className="download-workflow-icon"><ProductIcon name="history" /></div>
                        <h3>Git·SVN 변경 파일 조회</h3>
                        <p>Git 커밋이나 SVN 리비전 범위를 정해 저장소의 변경 파일을 불러옵니다.</p>
                    </article>
                    <article data-reveal>
                        <span>02</span>
                        <div className="download-workflow-icon"><ProductIcon name="package" /></div>
                        <h3>배포 ZIP · 스크립트 생성</h3>
                        <p>필요한 파일만 선택해 서버 경로 구조를 유지한 ZIP과 patch.sh를 만듭니다.</p>
                    </article>
                    <article data-reveal>
                        <span>03</span>
                        <div className="download-workflow-icon"><ProductIcon name="server" /></div>
                        <h3>SSH 서버 전송</h3>
                        <p>완성된 ZIP 패키지를 지정한 서버 경로로 SFTP 전송합니다.</p>
                    </article>
                    <article data-reveal>
                        <span>04</span>
                        <div className="download-workflow-icon"><ProductIcon name="terminal" /></div>
                        <h3>배포 · 백업 · 복구</h3>
                        <p>SSH 터미널에서 생성된 스크립트를 실행하고 결과를 로컬 배포 이력에 남깁니다.</p>
                    </article>
                </div>
            </section>

            <section className="download-workflow download-guide" id="installation" aria-labelledby="installation-title">
                <div className="download-section-heading">
                    <span>설치 안내</span>
                    <h2 id="installation-title">처음 사용하는 경우</h2>
                    <p>Windows 10/11 PC에서 설치하고, 패키징할 프로젝트의 로컬 경로를 준비하세요.</p>
                </div>
                <ol className="download-guide-steps">
                    <li><strong>설치 파일 다운로드</strong><p>상단의 Windows용 DeployKit 다운로드 버튼으로 설치 파일을 받은 뒤 실행합니다.</p></li>
                    <li><strong>프로젝트 경로 등록</strong><p>경로 관리에서 Git 또는 SVN 작업 사본, 일반 폴더 중 사용할 로컬 프로젝트를 등록합니다.</p></li>
                    <li><strong>패키징 조건 설정</strong><p>대상 서버의 배포 경로를 지정합니다. Java 소스를 컴파일하려면 프로젝트에서 사용하는 JDK 경로도 설정합니다.</p></li>
                    <li><strong>SSH 연결 준비</strong><p>서버로 전송하려면 SSH 프로필에 접속 정보와 인증 방법을 등록합니다. 서버 연결 전에도 파일을 선택해 ZIP을 만들 수 있습니다.</p></li>
                </ol>
            </section>

            <section className="download-workflow download-guide" id="example" aria-labelledby="example-title">
                <div className="download-section-heading">
                    <span>사용 예시</span>
                    <h2 id="example-title">수정한 Java 소스와 설정 파일만 배포하기</h2>
                    <p>예를 들어 Git 프로젝트에서 Java 소스와 설정 파일을 수정했다면, 이번 배포에 필요한 파일만 선택해 패키징할 수 있습니다.</p>
                </div>
                <div className="download-guide-example">
                    <ol>
                        <li>등록한 프로젝트에서 날짜와 Git 커밋을 선택해 변경 파일을 조회합니다.</li>
                        <li>이번 배포에 필요한 Java 소스와 설정 파일을 선택합니다.</li>
                        <li>지정한 JDK로 Java 소스를 컴파일하고 서버 경로 구조에 맞는 ZIP을 만듭니다.</li>
                        <li>ZIP 내부의 파일과 경로를 확인한 뒤 SSH 프로필로 SFTP 전송합니다.</li>
                    </ol>
                    <h3>생성되는 결과물</h3>
                    <p>선택한 소스의 컴파일 결과와 리소스를 담은 배포 ZIP, 배포·백업·복구 작업을 위한 patch.sh를 생성합니다. 실제 구성은 프로젝트와 패키징 설정에 따라 달라집니다.</p>
                    <p>서버에 전송한 뒤 SSH 터미널에서 생성된 스크립트를 실행할 수 있습니다. 이전 패키징·전송 결과는 배포 이력에서 확인합니다.</p>
                </div>
                <div className="download-guide-faq">
                    <h3>Git이나 SVN을 사용하지 않는 폴더도 가능한가요?</h3>
                    <p>일반 폴더도 등록할 수 있습니다. 로컬 파일의 수정일을 기준으로 조회하고 필요한 항목을 선택합니다.</p>
                    <h3>프로젝트 파일이 운영 웹사이트로 업로드되나요?</h3>
                    <p>프로젝트 분석과 패키징은 설치된 PC에서 실행됩니다. SSH/SFTP 전송은 사용자가 등록한 서버로 직접 연결합니다.</p>
                    <a href="https://github.com/Backjinuk/DeployKit-Desktop#readme" target="_blank" rel="noreferrer">GitHub에서 상세 사용 안내 보기 ↗</a>
                </div>
            </section>

            <footer className="download-contact">
                <a className="download-footer-brand" href="#top">
                    <img src={deployKitIcon} alt="" aria-hidden="true" />
                    <span>
                        <strong>DeployKit</strong>
                        <small>Deployment workflow for Windows</small>
                    </span>
                </a>
                <p>© 2026 DeployKit</p>
                <div>
                    <a href="https://github.com/Backjinuk/DeployKit-Desktop" target="_blank" rel="noreferrer">GitHub ↗</a>
                    <a href="mailto:backj123@naver.com">문의하기</a>
                </div>
            </footer>
        </main>
    );
};

export default DownloadPage;
